/**
 * Hook encapsulating AI sender generation state + handlers.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useCallback } from 'react';
import { useGenerateSenders } from '@/features/campaign-texts/hooks/useGenerateSenders';
import type { AiGenerateSendersMode } from '@/features/campaign-texts/components/AiGenerateSendersDropdown';

interface AiSenderGenerationParams {
  countryKey: string;
  onAddSenders: (senders: string[]) => Promise<void>;
  onSaveGeneratedAndSelectOnly?: (senders: string[]) => void;
  onMarkSendersForHide?: (senders: string[]) => void;
  onSaveGeneratedPerDrop?: (batches: string[][]) => Promise<void>;
  onPerDropSendersApplied?: (batches: string[][]) => void;
  onAiGeneratedDeselectOld?: () => void;
  /** Called after Save or Use & Hide — collapse senders, give space to texts. */
  onAfterSave?: () => void;
}

export function useAiSenderGeneration(params: AiSenderGenerationParams) {
  const {
    countryKey,
    onAddSenders,
    onSaveGeneratedAndSelectOnly,
    onMarkSendersForHide,
    onSaveGeneratedPerDrop,
    onPerDropSendersApplied,
    onAiGeneratedDeselectOld,
    onAfterSave: _onAfterSave,
  } = params;
  const [generatedSenders, setGeneratedSenders] = useState<string[]>([]);
  const [perDropBatches, setPerDropBatches] = useState<string[][] | null>(null);
  const [perDropApplied, setPerDropApplied] = useState(false);
  const [perDropGenerationTimeMs, setPerDropGenerationTimeMs] = useState<number | undefined>();
  const [lastGroupId, setLastGroupId] = useState('');
  const [groupIdsPerDrop, setGroupIdsPerDrop] = useState<string[]>([]);
  const regenMutation = useGenerateSenders();

  const handleAiGenerated = useCallback(
    (
      senders: string[] | string[][],
      gId: string,
      mode: AiGenerateSendersMode,
      durationMs?: number,
      _idsPerDrop?: string[],
    ) => {
      onAiGeneratedDeselectOld?.();
      setLastGroupId(gId);
      setGroupIdsPerDrop(_idsPerDrop ?? []);
      setPerDropApplied(false);
      if (mode === 'perDrop' && Array.isArray(senders[0])) {
        setPerDropBatches(senders as string[][]);
        setPerDropGenerationTimeMs(durationMs);
        setGeneratedSenders([]);
      } else {
        setGeneratedSenders(senders as string[]);
        setPerDropBatches(null);
        setPerDropGenerationTimeMs(undefined);
      }
    },
    [onAiGeneratedDeselectOld],
  );

  const handleSaveGenerated = useCallback(
    async (senders: string[]) => {
      onSaveGeneratedAndSelectOnly?.(senders);
      await onAddSenders(senders);
      setGeneratedSenders([]);
      /* Do not call onAfterSave — keep senders section open. */
    },
    [onAddSenders, onSaveGeneratedAndSelectOnly],
  );

  const handleSaveAndHide = useCallback(
    async (selectedSenders: string[]) => {
      onSaveGeneratedAndSelectOnly?.(selectedSenders);
      await onAddSenders(selectedSenders);
      if (selectedSenders.length > 0) onMarkSendersForHide?.(selectedSenders);
      setGeneratedSenders([]);
      /* Do not call onAfterSave — keep senders section open. */
    },
    [onAddSenders, onSaveGeneratedAndSelectOnly, onMarkSendersForHide],
  );

  const handleRegenerate = useCallback(
    (indices: number[]) => {
      if (!lastGroupId || indices.length === 0) return;
      regenMutation.mutate(
        { groupId: lastGroupId, countryKey, amount: indices.length },
        {
          onSuccess: (data) => {
            const replaceSet = new Set(indices);
            setGeneratedSenders((prev) => {
              let ri = 0;
              return prev.map((s, i) => {
                if (replaceSet.has(i) && ri < data.senders.length) return data.senders[ri++];
                return s;
              });
            });
          },
        },
      );
    },
    [lastGroupId, countryKey, regenMutation],
  );

  const handleDismiss = useCallback(() => {
    setGeneratedSenders([]);
    setPerDropBatches(null);
  }, []);

  const handleSavePerDrop = useCallback(
    async (batches: string[][]) => {
      if (!onSaveGeneratedPerDrop) return;
      const allSenders = batches.flat();
      await onAddSenders(allSenders);
      await onSaveGeneratedPerDrop(batches);
      setPerDropBatches(null);
      /* Do not call onAfterSave — keep senders section open. */
    },
    [onAddSenders, onSaveGeneratedPerDrop],
  );

  const handleDismissPerDrop = useCallback(() => {
    setPerDropBatches(null);
    setPerDropApplied(false);
    setGroupIdsPerDrop([]);
    setPerDropGenerationTimeMs(undefined);
  }, []);

  const handleRegeneratePerDrop = useCallback(
    (dropIdx: number, indices: number[]) => {
      const gId = groupIdsPerDrop[dropIdx] ?? lastGroupId;
      if (!gId || indices.length === 0 || !perDropBatches?.[dropIdx]) return;
      regenMutation.mutate(
        { groupId: gId, countryKey, amount: indices.length },
        {
          onSuccess: (data) => {
            setPerDropBatches((prev) => {
              if (!prev) return prev;
              const batch = [...prev[dropIdx]];
              let ri = 0;
              for (const i of indices) {
                if (ri < data.senders.length) batch[i] = data.senders[ri++];
              }
              const next = [...prev];
              next[dropIdx] = batch;
              return next;
            });
          },
        },
      );
    },
    [lastGroupId, groupIdsPerDrop, countryKey, regenMutation, perDropBatches],
  );

  const handleRegenerateAllPerDrop = useCallback(
    async (dropIndices: Array<{ dropIdx: number; indices: number[] }>) => {
      if (dropIndices.length === 0) return;
      for (const { dropIdx, indices } of dropIndices) {
        if (indices.length === 0) continue;
        const gId = groupIdsPerDrop[dropIdx] ?? lastGroupId;
        if (!gId) continue;
        const data = await regenMutation.mutateAsync({
          groupId: gId,
          countryKey,
          amount: indices.length,
        });
        setPerDropBatches((prev) => {
          if (!prev) return prev;
          const batch = [...prev[dropIdx]];
          let ri = 0;
          for (const i of indices) {
            if (ri < data.senders.length) batch[i] = data.senders[ri++];
          }
          const next = [...prev];
          next[dropIdx] = batch;
          return next;
        });
      }
    },
    [lastGroupId, groupIdsPerDrop, countryKey, regenMutation],
  );

  const handleSaveAndHidePerDrop = useCallback(
    async (batches: string[][], _unselectedSenders: string[]) => {
      if (!onSaveGeneratedPerDrop) return;
      const allSenders = batches.flat();
      onSaveGeneratedAndSelectOnly?.(allSenders);
      onPerDropSendersApplied?.(batches);
      await onAddSenders(allSenders);
      await onSaveGeneratedPerDrop(batches);
      if (allSenders.length > 0) onMarkSendersForHide?.(allSenders);
      setPerDropBatches(batches);
      setPerDropApplied(true);
      /* Do not call onAfterSave — keep senders section open. */
    },
    [
      onAddSenders,
      onSaveGeneratedAndSelectOnly,
      onSaveGeneratedPerDrop,
      onPerDropSendersApplied,
      onMarkSendersForHide,
    ],
  );

  return {
    generatedSenders,
    perDropBatches,
    perDropApplied,
    perDropGenerationTimeMs,
    groupIdsPerDrop,
    lastGroupId,
    isRegenerating: regenMutation.isPending,
    handleAiGenerated,
    handleSaveGenerated,
    handleSaveAndHide,
    handleRegenerate,
    handleDismiss,
    handleSavePerDrop,
    handleSaveAndHidePerDrop,
    handleDismissPerDrop,
    handleRegeneratePerDrop,
    handleRegenerateAllPerDrop,
  };
}
